import { Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Carousel } from 'react-bootstrap';
import { 
  Monitor, 
  Megaphone,
  Code, 
  Cloud, 
  Brain, 
  BarChart3, 
  Building, 
  GraduationCap,
  ShieldCheck,
  Users,
  Award,
  Clock,
  CheckCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ServiceCard } from '../components/common/ServiceCard';
import { PartnerLogo } from '../components/common/PartnerLogo';
import { StatCard } from '../components/common/StatCard';

const Home = () => {
  const { t, language } = useLanguage();

  const services = [
    { icon: Monitor, title: t('services.itConsulting'), description: t('services.itConsultingDesc') },
    { icon: Code, title: t('services.software'), description: t('services.softwareDesc') },
    { icon: Cloud, title: t('services.cloud'), description: t('services.cloudDesc') },
    { icon: Brain, title: t('services.ai'), description: t('services.aiDesc') },
    { icon: BarChart3, title: t('services.erp'), description: t('services.erpDesc') },
    { icon: Building, title: t('services.govEnterprise'), description: t('services.govEnterpriseDesc') }
  ];

  const partners = [
    language === 'hi' ? 'सरकारी विभाग' : 'Government Department',
    language === 'hi' ? 'राज्य संस्थान' : 'State Institution',
    language === 'hi' ? 'विश्वविद्यालय' : 'University',
    language === 'hi' ? 'सार्वजनिक क्षेत्र संगठन' : 'Public Sector Organization'
  ];

  const whyChooseData = [
    { 
      iconName: 'ShieldCheck', 
      title: language === 'hi' ? 'विश्वसनीय और सुरक्षित' : 'Trusted & Secure',
      description: language === 'hi' ? 'GIGW अनुपालन समाधान' : 'GIGW compliant solutions'
    },
    { 
      iconName: 'Users', 
      title: language === 'hi' ? 'विशेषज्ञ टीम' : 'Expert Team',
      description: language === 'hi' ? 'अनुभवी पेशेवर' : 'Experienced professionals'
    },
    { 
      iconName: 'Award', 
      title: language === 'hi' ? 'गुणवत्ता आश्वासन' : 'Quality Assured',
      description: language === 'hi' ? 'उच्च मानक समाधान' : 'High standard solutions'
    },
    { 
      iconName: 'Clock', 
      title: language === 'hi' ? 'समय पर डिलीवरी' : 'Timely Delivery',
      description: language === 'hi' ? 'समय पर परियोजना पूर्णता' : 'On-time project completion'
    }
  ];
  
  const iconComponents = { ShieldCheck, Users, Award, Clock };

  const trainingHighlights = [
    language === 'hi' ? 'कृत्रिम बुद्धिमत्ता और मशीन लर्निंग' : 'Artificial Intelligence & ML',
    language === 'hi' ? 'फुल स्टैक डेवलपमेंट' : 'Full Stack Development',
    language === 'hi' ? 'क्लाउड कंप्यूटिंग' : 'Cloud Computing',
    language === 'hi' ? 'डेटा साइंस' : 'Data Science'
  ];

  return (
    <main id="main-content">
{/* Hero Section */}
    <section className="gov-hero">
      <Carousel
        fade
        interval={3000}
        indicators={true}
        controls={true}
        pause="hover"
      >

        {/* SLIDE 1 – Government */}
        <Carousel.Item>
          <div
            className="gov-hero-slide"
            style={{
              backgroundImage:
                "linear-gradient(rgba(5, 25, 55, 0.85), rgba(5, 25, 55, 0.85)), url('/images/slider/1.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: "70vh",
              display: "flex",
              alignItems: "center"
            }}
          >
            <Container>
              <Row className="align-items-center justify-content-center">
                <Col lg={8} className="text-center">

                  <div className="hero-badge mb-3 text-white">
                    <Sparkles size={16} /> {t('home.tagline')}
                  </div>

                  <h1 className="hero-title text-white fw-bold">
                    {t('home.heroTitle')}
                  </h1>

                  <p className="hero-subtitle text-light">
                    {t('home.heroSubtitle')}
                  </p>

                  <div className="hero-actions mt-4">
                    <Link to="/services">
                      <button className="hero-btn hero-btn-primary me-2">
                        Explore Our Solutions
                        <ArrowRight size={18} />
                      </button>
                    </Link>

                    <Link to="/contact">
                      <button className="hero-btn hero-btn-primary">
                        Contact Us
                      </button>
                    </Link>
                  </div>

                </Col>
              </Row>
            </Container>
          </div>
        </Carousel.Item>

        {/* SLIDE 2 – Education */}
        <Carousel.Item>
          <div
            className="gov-hero-slide"
            style={{
              backgroundImage:
                "linear-gradient(rgba(5, 25, 55, 0.85), rgba(5, 25, 55, 0.85)), url('/images/slider/3.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: "70vh",
              display: "flex",
              alignItems: "center"
            }}
          >
            <Container>
              <Row className="align-items-center justify-content-center">
                <Col lg={8} className="text-center">

                  <h1 className="hero-title text-white fw-bold">
                    Education & Skill Development Programs
                  </h1>

                  <p className="hero-subtitle text-light">
                    Industry-aligned training in AI, Cloud, Full-Stack and Data Science
                    for students & institutions.
                  </p>

                  <div className="hero-actions mt-4">
                    <Link to="/training">
                      <button className="hero-btn hero-btn-primary">
                        View Training Programs
                        <ArrowRight size={18} />
                      </button>
                    </Link>
                  </div>

                </Col>
              </Row>
            </Container>
          </div>
        </Carousel.Item>

        {/* SLIDE 3 – Enterprise */}
        <Carousel.Item>
          <div
            className="gov-hero-slide"
            style={{
              backgroundImage:
                "linear-gradient(rgba(5, 25, 55, 0.85), rgba(5, 25, 55, 0.85)), url('/images/slider/2.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: "70vh",
              display: "flex",
              alignItems: "center"
            }}
          >
            <Container>
              <Row className="align-items-center justify-content-center">
                <Col lg={8} className="text-center">

                  <h1 className="hero-title text-white fw-bold">
                    Enterprise, AI & Digital Transformation
                  </h1>

                  <p className="hero-subtitle text-light">
                    ERP, AI automation, cloud migration & enterprise-grade digital
                    solutions.
                  </p>

                  <div className="hero-actions mt-4">
                    <Link to="/services">
                      <button className="hero-btn hero-btn-primary">
                        Discover Solutions
                        <ArrowRight size={18} />
                      </button>
                    </Link>
                  </div>

                </Col>
              </Row>
            </Container>
          </div>
        </Carousel.Item>

      </Carousel>
    </section>

      {/* 🔥 CLIENTS AUTO SCROLL WITH TOOLTIP */}
      <section className=" gov-section-alt">
      <Container>
        {/* <div className="text-center mb-5">
          <h2 className="gov-section-heading">
            Trusted By Clients & Institutions
          </h2>
        </div> */}

        <div className="logo-slider">
          <div className="logo-track">

            {[
             
              {
                name: "Balaji Electricals",
                logo: "/images/logos/balaji_electricals.png",
                link: "#"
              },
              {
                name: "Freighter Biz HongKong",
                logo: "/images/logos/Logo_Freighters.png",
                link: "#"
              },
              {
                name: "Manav Hospital",
                logo: "/images/logos/logo-manav-1.jpg",
                link: "#"
              },
              {
                name: "Zigly Pet Care",
                logo: "/images/logos/images.png",
                link: "#"
              },
               {
                name: "IIT DELHI",
                logo: "/images/logos/logo.png",
                link: "#"
              },
              {
                name: "Posh Compliance",
                logo: "/images/logos/venture_mitra_posh.png",
                link: "#"
              }
            ].map((client, i) => (
              <div className="logo-item" key={i}>
                <div className="logo-inner" data-name={client.name}>
                  <a href={client.link} target="_blank" rel="noopener noreferrer">
                    <img src={client.logo} alt={client.name} />
                  </a>
                </div>
              </div>
            ))}

            {/* 🔁 DUPLICATE FOR SMOOTH LOOP */}
            {[
              {
                name: "Divyam Interior",
                logo: "/images/logos/divyam_interir.png",
                link: "#"
              },
              {
                name: "DEV Group",
                logo: "/images/logos/dev_group.png",
                link: "#"
              },
              {
                name: "Vicintity Lounge & BAR Canada",
                logo: "/images/logos/vicinitylogo.png",
                link: "#"
              },
            
             
            ].map((client, i) => (
              <div className="logo-item" key={`dup-${i}`}>
                <div className="logo-inner" data-name={client.name}>
                  <a href={client.link} target="_blank" rel="noopener noreferrer">
                    <img src={client.logo} alt={client.name} />
                  </a>
                </div>
              </div>
            ))}

          </div>
        </div>
      </Container>
      </section>
      
      {/* What We Build Section */}
      {/* <section className="what-we-build">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="build-items">
                <div className="build-item">
                  <Monitor size={26} />
                  <span>Web Platforms</span>
                </div>

                <div className="build-item">
                  <Brain size={26} />
                  <span>AI Solutions</span>
                </div>

                <div className="build-item">
                  <Cloud size={26} />
                  <span>Cloud & DevOps</span>
                </div>

                <div className="build-item">
                  <BarChart3 size={26} />
                  <span>ERP Systems</span>
                </div>

                <div className="build-item">
                  <Megaphone size={26} />
                  <span>Digital Marketing</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section> */}
      {/* Introduction Section */}
      <section className="gov-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="text-center">
                <h2 className="gov-section-heading">
                  {t('home.introTitle')}
                </h2>
                <p style={{ 
                  fontSize: '1.0625rem', 
                  color: 'hsl(var(--muted-foreground))',
                  lineHeight: 1.7,
                  maxWidth: '800px',
                  margin: '0 auto'
                }}>
                  {t('home.introText')}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Section */}
      <section className="gov-section gov-section-alt">
        <Container>
          <div className="text-center mb-5">
            <h2 className="gov-section-heading">
              {t('home.servicesTitle')}
            </h2>
            <p style={{ color: 'hsl(var(--muted-foreground))' }}>
              {t('home.servicesSubtitle')}
            </p>
          </div>

          <Row className="g-4">
            {services.map((service, index) => (
              <Col key={index} md={6} lg={4}>
                <ServiceCard {...service} />
              </Col>
            ))}
          </Row>

          <div className="text-center mt-5">
            <Link to="/services">
              <Button className="btn-gov-primary d-inline-flex align-items-center gap-2">
                {t('common.viewAll')} {t('nav.services')}
                <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </Container>
      </section>
       {/* Stats Section */}
      {/* <section style={{ 
        background: 'hsl(var(--background))', 
        borderBottom: '1px solid hsl(var(--border))',
        marginTop: '-2rem',
        position: 'relative',
        zIndex: 10
      }}>
        <Container>
          <div 
            style={{
              background: 'hsl(var(--card))',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              padding: '1.5rem'
            }}
          >
            <Row className="g-0">
              <Col xs={6} md={3}>
                <StatCard 
                  number="7+" 
                  label={language === 'hi' ? 'सेवाएं' : 'Services'} 
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard 
                  number="10+" 
                  label={language === 'hi' ? 'प्रशिक्षण कार्यक्रम' : 'Training Programs'} 
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard 
                  number="100%" 
                  label={language === 'hi' ? 'समर्पण' : 'Commitment'} 
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard 
                  number="24/7" 
                  label={language === 'hi' ? 'सहायता' : 'Support'} 
                />
              </Col>
            </Row>
          </div>
        </Container>
      </section> */}

      {/* Training Section */}
      <section className="gov-section">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              <h2 className="gov-section-heading" style={{ textAlign: 'left' }}>
                {t('home.trainingTitle')}
              </h2>
              <p style={{ 
                color: 'hsl(var(--muted-foreground))',
                marginBottom: '1.5rem',
                fontSize: '1.0625rem'
              }}>
                {t('home.trainingDescription')}
              </p>

              <ul style={{ listStyle: 'none', padding: 0 }}>
                {trainingHighlights.map((item, index) => (
                  <li 
                    key={index}
                    className="d-flex align-items-center gap-2 mb-3"
                  >
                    <div 
                      style={{
                        width: '24px',
                        height: '24px',
                        background: 'hsl(var(--success) / 0.1)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CheckCircle size={14} style={{ color: 'hsl(var(--success))' }} />
                    </div>
                    <span style={{ fontWeight: '500' }}>{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/training">
                <Button className="btn-gov-primary mt-3 d-inline-flex align-items-center gap-2">
                  {t('home.viewPrograms')}
                  <ArrowRight size={18} />
                </Button>
              </Link>
            </Col>

            <Col lg={6}>
              <div 
                style={{
                  background: 'var(--gradient-gov-banner)',
                  borderRadius: 'var(--radius-lg)',
                  padding: '3rem 2rem',
                  color: 'white',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <GraduationCap size={48} style={{ marginBottom: '1.5rem', opacity: 0.9 }} />
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    {language === 'hi' ? 'कौशल विकास कार्यक्रम' : 'Skill Development Programs'}
                  </h3>
                  <p style={{ opacity: 0.9, marginBottom: '1.5rem' }}>
                    {language === 'hi' 
                      ? 'छात्रों और संकाय के लिए उद्योग-प्रासंगिक प्रशिक्षण' 
                      : 'Industry-relevant training for students and faculty'}
                  </p>
                  <div className="d-flex flex-wrap gap-2">
                    <span 
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '50px',
                        fontSize: '0.75rem'
                      }}
                    >
                      {language === 'hi' ? 'प्रमाण पत्र' : 'Certification'}
                    </span>
                    <span 
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '50px',
                        fontSize: '0.75rem'
                      }}
                    >
                      {language === 'hi' ? 'प्लेसमेंट सहायता' : 'Placement Support'}
                    </span>
                    <span 
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        padding: '0.375rem 0.75rem',
                        borderRadius: '50px',
                        fontSize: '0.75rem'
                      }}
                    >
                      {language === 'hi' ? 'व्यावहारिक प्रशिक्षण' : 'Hands-on Training'}
                    </span>
                  </div>
                </div>
                
                {/* Decorative Pattern */}
                <div 
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '200px',
                    height: '200px',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    transform: 'translate(50%, -50%)'
                  }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Partners Section */}
      <section className="gov-section gov-section-alt">
        <Container>
          <div className="text-center mb-5">
            <h2 className="gov-section-heading">
              {t('home.partnersTitle')}
            </h2>
            <p style={{ color: 'hsl(var(--muted-foreground))' }}>
              {t('home.partnersSubtitle')}
            </p>
          </div>

          <Row className="g-4 justify-content-center">
            {partners.map((partner, index) => (
              <Col key={index} xs={6} md={3}>
                <PartnerLogo name={partner} />
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why Choose Section */}
      <section className="gov-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="gov-section-heading">
              {t('home.whyTitle')}
            </h2>
            <p style={{ color: 'hsl(var(--muted-foreground))' }}>
              {t('home.whySubtitle')}
            </p>
          </div>

          <Row className="g-4">
            {whyChooseData.map((item, index) => {
              const IconComponent = iconComponents[item.iconName];
              return (
                <Col key={index} md={6} lg={3}>
                  <div 
                    className="d-flex align-items-start p-4"
                    style={{
                      background: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 'var(--radius-lg)',
                      height: '100%',
                      transition: 'box-shadow 0.25s ease'
                    }}
                  >
                    <div className="why-icon">
                      <IconComponent size={24} />
                    </div>
                    <div>
                      <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.375rem' }}>
                        {item.title}
                      </h4>
                      <p style={{ 
                        fontSize: '0.875rem', 
                        color: 'hsl(var(--muted-foreground))',
                        marginBottom: 0
                      }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>
    

      {/* CTA Section */}
      <section className="cta-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center">
              <h2 style={{ fontSize: '1.75rem', fontWeight: '600', marginBottom: '1rem' }}>
                {t('home.ctaTitle')}
              </h2>
              <p style={{ opacity: 0.9, marginBottom: '2rem', fontSize: '1.0625rem' }}>
                {t('home.ctaSubtitle')}
              </p>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Link to="/contact">
                  <Button className="btn-gov-light d-inline-flex align-items-center gap-2">
                    {t('home.ctaButton')}
                    <ArrowRight size={18} />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button 
                    variant="outline-light"
                    style={{ borderWidth: '2px', fontWeight: '500' }}
                  >
                    {t('home.learnMore')}
                  </Button>
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
      

    


    </main>
  );
};

export default Home;
